import { ListPage } from '@components/molecules';
import { DatePickerElement } from 'react-hook-form-mui/date-pickers';
import { Grid } from '@mui/material';
import API from '@apps/nnc/services/nnc';
import { ModelProps } from '@/types';
import { useTranslation } from 'react-i18next';
import { JSX, useEffect, useState } from 'react';
import {
  NncSchema,
  NncSearchSchema,
  NncSearchSchemaType,
} from '@apps/nnc/schemas/nnc';
import AsyncAutocompleteElement from '@/components/atoms/asyncautocomplete';
import CountryAPI from '@apps/localization/services/country';
import UnitAPI from '@apps/localization/services/unit';
import {
  AutocompleteElement,
  TextFieldElement,
  useForm,
} from 'react-hook-form-mui';
import DivisionAPI from '@apps/localization/services/division';

export default function NncListPage(modelProps: ModelProps): JSX.Element {
  const { t } = useTranslation();
  const formContext = useForm<NncSearchSchemaType>({
    defaultValues: {
      status: [
        {
          id: 1,
          label: t('Pending'),
        },
      ],
    },
  });
  const [refetch, setRefresh] = useState(false);
  const start = formContext.watch('start_date', null);
  const end = formContext.watch('end_date', null);
  const opt = [
    {
      id: 1,
      label: t('Pending'),
    },
    {
      id: 2,
      label: t('Draft'),
    },
    {
      id: 3,
      label: t('Already Rated'),
    },
    {
      id: 4,
      label: t('Critical Analysis'),
    },
  ];
  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      hide: true,
    },
    {
      field: 'name',
      headerName: t('Name'),
      flex: 1,
    },
  ];

  useEffect(() => {
    if (!start && !end) {
      setRefresh(!refetch);
    }
  }, [start, end]);

  return (
    <ListPage
      formcontext={formContext}
      schema={NncSchema}
      schemaSearch={NncSearchSchema}
      title={t('NNC Management')}
      api={API}
      columns={columns}
      modelProps={modelProps}
      customPath={'nnc/management'}
      cards
      disableAdd
      effect={refetch}
      enablePersist={true}
      persistKey="nnc.management.list"
    >
      <Grid size={{ xs: 12, md: 2.8 }}>
        <TextFieldElement name="search" label={t('Search')} fullWidth />
      </Grid>
      <Grid size={{ xs: 12, md: 2.8 }}>
        <DatePickerElement
          name="start_date"
          label={t('Start Date')}
          inputProps={{ sx: { width: '100%' } }}
          slotProps={{
            textField: {
              fullWidth: true,
              error: false,
            },
            actionBar: {
              actions: ['clear', 'today'],
            },
          }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 2.8 }}>
        <DatePickerElement
          name="end_date"
          label={t('End Date')}
          inputProps={{ sx: { width: '100%' } }}
          slotProps={{
            textField: {
              fullWidth: true,
              error: false,
            },
            actionBar: {
              actions: ['clear', 'today'],
            },
          }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 2.8 }}>
        <AsyncAutocompleteElement
          api={CountryAPI.select}
          name="country"
          label={t('Country')}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 2.8 }}>
        <AsyncAutocompleteElement
          api={UnitAPI.select}
          name="unit"
          label={t('Unit')}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 2.8 }}>
        <AsyncAutocompleteElement
          api={() =>
            DivisionAPI.select({
              is_active: 1,
            })
          }
          name="division"
          label={t('Identified Area')}
          multiple
          fetchOnMount
        />
      </Grid>
      <Grid size={{ xs: 12, md: 2.8 }}>
        <TextFieldElement name="operation" label={t('Operation')} fullWidth />
      </Grid>
      <Grid size={{ xs: 12, md: 2.8 }}>
        <AutocompleteElement
          name="status"
          label={t('Status')}
          multiple
          options={opt}
        />
      </Grid>
    </ListPage>
  );
}
